import api from "../../../../../../api";
import React, { useEffect } from "react";
import utils from "../../../../../../utils";
import values from "../../../../../../values";
import Context from "../../../../../../context";
import TitleLine from "../../../../../../components/TitleLine";

import * as Yup from "yup";

import { useFormik } from "formik";
import { useQuery } from "react-query";
import {
  ICityItem,
  IOnSearchUser,
  IProvinceItem,
} from "../../../../../../types/common.interface";
import { ICreateAndUpdateCompany } from "../../../../../../types/company.interface";
import { ICreateAndUpdateSuperAgent } from "../../../../../../types/superAgent.interface";
import {
  CustomButton2,
  CustomCheckBox,
  CustomDropdownMultiSearch,
  CustomDropdownSearch,
  CustomInput,
  CustomView,
  ImageUploader,
} from "../../../../../../components";
import validations from "../../../../../../validations";
import { uploadImage } from "../../../../../../api/method.post/common";
import { useTranslation } from "react-i18next";
// import validations from "../../../../../../validations";

const Administrator = () => {
  // ** ===========================================================================================================
  // **                                            Value Validation
  // ** -----------------------------------------------------------------------------------------------------------
  const { setShowModal, updateFunction, isLoading, selectedCompany } =
    React.useContext(Context.CompanyScreen);
  const [selectedSuperAgent, setSelectedSuperAgent] =
    React.useState<ICreateAndUpdateSuperAgent>(selectedCompany?.companyInformation?.superAgent);
  const [isDriverWallet, setIsDriverWallet] = React.useState<boolean>(false);
  const [isAdminPozMachine, setIsAdminPozMachine] =
    React.useState<boolean>(false);

  const [selectedCities, setSelectedCities] = React.useState<ICityItem[]>([]);
  const [selectedProvinces, setSelectedProvinces] = React.useState<
    IProvinceItem[]
  >([]);
  const [selectedProvinceCode, setSelectedProvinceCode] = React.useState<
    string | undefined
  >();
  const [searchedValue, setSearchedValue] = React.useState<IOnSearchUser>();
  const [dropDownErrors, setDropDownErrors] = React.useState<any>({
    province: "",
    city: "",
    superAgent: "",
  });

  const [selectedFinancialGroup, setSelectedFinancialGroup] =
    React.useState<any>(undefined);
  const [selectedNationalCardImage, setSelectedNationalCardImage] =
    React.useState<any>();
  const [selectedNationalCardBackImage, setSelectedNationalCardBackImage] =
    React.useState<any>();
  const [selectedCompanyLicenseImage, setSelectedCompanyLicenseImage] =
    React.useState<any>();

  const { t } = useTranslation();

  // !! ===========================================================================================================
  // !!                                            Start API Call
  // !! -----------------------------------------------------------------------------------------------------------
  const getAllProvince = useQuery([values.useQueryKeys.getAllProvince], () =>
    api.get.common.getAllProvince()
  );
  const getAllCity = useQuery(
    [values.useQueryKeys.getAllCity, selectedProvinceCode],
    () =>
      api.get.common.getAllCity({
        provinceCode: selectedProvinceCode,
      })
  );
  const getAllSuperAgent = useQuery(
    [values.useQueryKeys.getAllSuperAgent, searchedValue],
    () =>
      api.post.administrator.getAllSuperAgent({
        search: {
          base: searchedValue?.base.value,
          value: searchedValue?.searchedValue,
        },
      })
  );

  const getAllFinancilalGroup = useQuery(
    [values.useQueryKeys?.getAllFinancialGroup],
    () => api.post.administrator.getAllFinancialGroup({})
  );

  console.log(getAllFinancilalGroup);

  const AllSuperAgentWithCompleteName = getAllSuperAgent?.data?.result?.map(
    (item: any) => {
      return {
        ...item,
        completeName: item.firstName + " " + item.lastName,
      };
    }
  );

  // !! -----------------------------------------------------------------------------------------------------------
  // !!                                             End API Call
  // !! ===========================================================================================================

  // ?? ===========================================================================================================
  // ??                                           Use Formik Function
  // ?? -----------------------------------------------------------------------------------------------------------
  React.useEffect(() => {
    // the company's own city/province documents use `name` / `code`; the dropdowns and city chips use the province API's
    // `Name` / `Code` / `ProvinceCode` (same _ids), so map them — otherwise the chips were blank and no province showed
    const savedProvinces: IProvinceItem[] = (selectedCompany?.companyInformation?.province ?? [])
      .filter((item: any) => item?._id)
      .map((item: any) => ({ ...item, Name: item.Name ?? item.name, Code: String(item.Code ?? item.code) }));
    const provinceCodeOf = (provinceId: string) =>
      savedProvinces.find((province) => province._id === provinceId)?.Code;
    setSelectedProvinces(savedProvinces);
    setSelectedCities(
      (selectedCompany?.companyInformation?.city ?? [])
        .filter((item: any) => item?._id)
        .map((item: any) => ({
          ...item,
          Name: item.Name ?? item.name,
          ProvinceCode: item.ProvinceCode ?? provinceCodeOf(item.province),
        }))
    );
    setSelectedProvinceCode(savedProvinces[0]?.Code);
    // the company's education office comes populated with it; searching the office list for it failed whenever the
    // list hadn't loaded yet or the office wasn't on its first page, which left the (required) dropdown empty
    const companySuperAgent = selectedCompany?.companyInformation?.superAgent;
    setSelectedSuperAgent(
      companySuperAgent?._id
        ? {
            ...companySuperAgent,
            completeName: companySuperAgent.firstName + " " + companySuperAgent.lastName,
          }
        : undefined
    );

    setSelectedCompanyLicenseImage({
      link: selectedCompany?.companyInformation?.documents?.companyLicence,
    });
    setSelectedNationalCardImage({
      link: selectedCompany?.companyInformation?.documents?.nationalCardFront,
    });
    setSelectedNationalCardBackImage({
      link: selectedCompany?.companyInformation?.documents?.nationalCardBack,
    });
    setIsDriverWallet(selectedCompany?.companyInformation?.isDriverWallet);
    setIsAdminPozMachine(
      selectedCompany?.companyInformation?.isAdminPozMachine
    );
  }, [selectedCompany]);

  const formik = useFormik<ICreateAndUpdateCompany>({
    enableReinitialize: true,
    initialValues: {
      _id: selectedCompany?._id ? selectedCompany._id : undefined,
      address: selectedCompany?.companyInformation?.address
        ? selectedCompany.companyInformation.address
        : undefined,
      lastName: selectedCompany?.lastName
        ? selectedCompany.lastName
        : undefined,
      firstName: selectedCompany?.firstName
        ? selectedCompany.firstName
        : undefined,
      kartNumber: selectedCompany?.financial?.kartNumber
        ? selectedCompany.financial?.kartNumber
        : undefined,
      phoneNumber: selectedCompany?.phoneNumber
        ? selectedCompany.phoneNumber
        : undefined,
      shabaNumber: selectedCompany?.financial?.shabaNumber
        ? selectedCompany.financial?.shabaNumber
        : undefined,
      companyName: selectedCompany?.companyInformation?.companyName
        ? selectedCompany.companyInformation?.companyName
        : undefined,
      nationalCode: selectedCompany?.companyInformation?.nationalCode
        ? selectedCompany.companyInformation?.nationalCode
        : undefined,
      nationalId: selectedCompany?.companyInformation?.nationalId
        ? selectedCompany.companyInformation?.nationalId
        : undefined,
      registrationNumber: selectedCompany?.companyInformation
        ?.registrationNumber
        ? selectedCompany.companyInformation?.registrationNumber
        : undefined,
    },
    validationSchema: Yup.object({
      address: validations.yupValidations.address,
      lastName: validations.yupValidations.lastName,
      firstName: validations.yupValidations.firstName,
      kartNumber: validations.yupValidations.kartNumber,
      phoneNumber: validations.yupValidations.phoneNumber,
      shabaNumber: validations.yupValidations.shabaNumber,
      companyName: validations.yupValidations.firstName,
      nationalCode: validations.yupValidations.nationalCode,
      registrationNumber: validations.yupValidations.registrationNumber,
      nationalId: validations.yupValidations.nationalId,
    }),
    onSubmit: (values: ICreateAndUpdateCompany, { setSubmitting }) => {
      //if (!selectedSuperAgent) return utils.errorHandler(9013);
      const selectedProvinceWithDuplicate = selectedProvinces?.map(
        (item) => item?._id as string
      );
      const formValues: ICreateAndUpdateCompany = {
        ...values,
        superAgent: selectedSuperAgent,
        city: selectedCities?.map((item) => item?._id as string),
        province: selectedProvinceWithDuplicate?.filter(
          (item, index) => selectedProvinceWithDuplicate.indexOf(item) === index
        ),
        financialGroupSchool: selectedFinancialGroup?._id,
        nationalCardFront: selectedNationalCardImage,
        nationalCardBack: selectedNationalCardBackImage,
        companyLicence: selectedCompanyLicenseImage,
        isDriverWallet,
        isAdminPozMachine,
      };
      if (
        selectedSuperAgent &&
        selectedCities.length !== 0 &&
        selectedFinancialGroup
      ) {
        Promise.resolve(updateFunction(formValues)).catch((e) =>
          setSubmitting(false)
        );
      }
    },
  });
  const submitHandler = () => {
    // validation

    let errors = {};
    if (selectedCities.length === 0) {
      errors = {
        ...errors,
        city: t("city_is_required"),
      };
    }
    if (!selectedSuperAgent) {
      errors = {
        ...errors,
        superAgent: t("super_agent_is_required"),
      };
    }
    if (!selectedFinancialGroup) {
      errors = {
        ...errors,
        financialGroup: t("financial_group_is_required"),
      };
    }
    setDropDownErrors(errors);

    formik.submitForm();
  };

  useEffect(() => {
    setSelectedFinancialGroup(
      selectedCompany?.companyInformation?.financialGroupSchool
    );
  }, [selectedCompany, getAllFinancilalGroup?.data?.result]);

  async function onUploadImage(
    imageFile: File,
    setImage: (image: any) => void
  ) {
    const imagePromise = await uploadImage!(imageFile);
    if (imagePromise?.CODE === 2042) setImage(imagePromise?.result);
  }

  // ?? ===========================================================================================================
  // ??                                              JSX Content
  // ?? -----------------------------------------------------------------------------------------------------------

  return (
    <>
      <CustomView.Container className="update-form-content">
        <TitleLine title={t("companyInfo")} />
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("superAgent")}</label>

            <CustomDropdownMultiSearch
              isSearch
              items={AllSuperAgentWithCompleteName}
              itemName="completeName"
              defaultItem={selectedSuperAgent}
              onSelectItem={(event: IProvinceItem) => {
                setSelectedSuperAgent(event);
                setDropDownErrors({ ...dropDownErrors, superAgent: "" });
              }}
              placeHolder={t("selectEducation")}
              baseSearch={values.dropdownBaseSearch.SUPER_AGENT_BASE_SEARCH.map(
                (item) => ({
                  ...item,
                  title: t(item.title),
                })
              )}
              setSearchedValue={setSearchedValue}
              searchedValue={searchedValue}
              isLoading={getAllSuperAgent.isLoading}
              selectedBaseSearch={searchedValue?.base?.value}
            />
            <div className="error">
              {dropDownErrors.superAgent !== "" && dropDownErrors.superAgent}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <CustomInput
              name="companyName"
              id="companyName"
              label={t("companyName")}
              value={formik.values.companyName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.companyName && formik.touched.companyName}
              errorText={formik.errors.companyName}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <CustomInput
              name="registrationNumber"
              id="registrationNumber"
              label={t("registrationNumber")}
              value={formik.values.registrationNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={
                formik.errors.registrationNumber &&
                formik.touched.registrationNumber
              }
              errorText={formik.errors.registrationNumber}
              maxLength={16}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <CustomInput
              name="nationalId"
              id="nationalId"
              label={t("nationalId")}
              value={formik.values.nationalId}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.nationalId && formik.touched.nationalId}
              errorText={formik.errors.nationalId}
              maxLength={11}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("province")}</label>
            <CustomDropdownSearch
              items={getAllProvince?.data?.result}
              itemName="Name"
              defaultItem={
                getAllProvince?.data?.result?.find(
                  (item: IProvinceItem) =>
                    selectedProvinceCode !== undefined && String(item.Code) === String(selectedProvinceCode)
                )
                // selectedProvinces[selectedProvinces.length - 1]
              }
              onSelectItem={(event: IProvinceItem) => {
                setSelectedProvinceCode(event?.Code);
                setDropDownErrors({ ...dropDownErrors, province: "" });
              }}
              placeHolder={t("selectProvince")}
              isSearch
            />
            <div className="error">
              {dropDownErrors.province !== "" && dropDownErrors.province}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("city")}</label>
            <CustomDropdownSearch
              items={getAllCity?.data?.result}
              itemName="Name"
              defaultItem={undefined}
              onSelectItem={(event: ICityItem) => {
                !selectedCities?.find((item) => item?._id === event?._id) &&
                  setSelectedCities((prev) => [
                    ...prev,
                    { ...event, ProvinceCode: selectedProvinceCode },
                  ]);

                !selectedProvinces?.find((item) => item?._id === event?._id) &&
                  setSelectedProvinces((prev) => [
                    ...(prev as IProvinceItem[]),
                    getAllProvince?.data?.result?.find(
                      (item: IProvinceItem) =>
                        String(item?.Code) === String(selectedProvinceCode)
                    ) as IProvinceItem,
                  ]);
                setDropDownErrors({ ...dropDownErrors, city: "" });
              }}
              placeHolder={t("selectCity")}
              isSearch
            />
            <div className="error">
              {dropDownErrors.city !== "" && dropDownErrors.city}
            </div>
          </CustomView.Column>
          <div className="city-items">
            {selectedCities?.map((item) => (
              <span>
                {item?.Name}{" "}
                <i
                  onClick={() => {
                    const remainingCities = selectedCities?.filter(
                      (filterItem) => filterItem._id !== item._id
                    );
                    setSelectedCities(remainingCities);
                    // keep exactly the provinces the remaining cities are in. (This used to splice the state array in
                    // place at indexOf(ProvinceCode) — always -1 — so removing any city deleted the last province.)
                    const remainingCodes = new Set(
                      remainingCities?.map((city) => String(city?.ProvinceCode))
                    );
                    setSelectedProvinces(
                      selectedProvinces?.filter((province) =>
                        remainingCodes.has(String(province?.Code))
                      )
                    );
                  }}
                  className="fa-solid fa-xmark close-mark"
                ></i>
              </span>
            ))}
          </div>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={12} className="form-colum">
            <CustomInput
              name="address"
              id="address"
              label={t("address")}
              value={formik.values.address}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.address && formik.touched.address}
              errorText={formik.errors.address}
            />
          </CustomView.Column>
        </CustomView.Row>
        <TitleLine title={t("companyManagerInfo")} />
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="firstName"
              id="firstName"
              label={t("firstName")}
              value={formik.values.firstName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.firstName && formik.touched.firstName}
              errorText={formik.errors.firstName}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="lastName"
              id="lastName"
              label={t("lastName")}
              value={formik.values.lastName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.lastName && formik.touched.lastName}
              errorText={formik.errors.lastName}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="nationalCode"
              id="nationalCode"
              label={t("nationalCode")}
              value={formik.values.nationalCode}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.nationalCode && formik.touched.nationalCode}
              errorText={formik.errors.nationalCode}
              maxLength={10}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="phoneNumber"
              id="phoneNumber"
              label={t("phoneNumber")}
              value={formik.values.phoneNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.phoneNumber && formik.touched.phoneNumber}
              errorText={formik.errors.phoneNumber}
              maxLength={11}
            />
          </CustomView.Column>
        </CustomView.Row>
        <TitleLine title={t("financialInfo")} />
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("financialGroup")}</label>
            <CustomDropdownSearch
              items={getAllFinancilalGroup?.data?.result}
              itemName="name"
              defaultItem={
                selectedFinancialGroup
                  ? selectedFinancialGroup
                  : getAllFinancilalGroup?.data?.result?.find(
                      (item: any) =>
                        item?._id ===
                        selectedCompany?.companyInformation
                          ?.financialGroupSchool
                    )
              }
              onSelectItem={(event: any) => {
                setSelectedFinancialGroup(event);
              }}
              placeHolder={t("selectFinancialGroup")}
            />
            <div className="error">
              {dropDownErrors.financialGroup !== "" &&
                dropDownErrors.financialGroup}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="kartNumber"
              id="kartNumber"
              label={t("cardNumber")}
              value={formik.values.kartNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.kartNumber && formik.touched.kartNumber}
              errorText={formik.errors.kartNumber}
              maxLength={16}
            />
          </CustomView.Column>
        </CustomView.Row>

        <CustomView.Row className="form-row">
          <CustomView.Column medium={12} className="form-colum">
            <CustomInput
              name="shabaNumber"
              id="shabaNumber"
              label={t("shabaNumber")}
              value={formik.values.shabaNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.shabaNumber && formik.touched.shabaNumber}
              errorText={formik.errors.shabaNumber}
              isShaba
              maxLength={24}
            />
          </CustomView.Column>
          <CustomView.Column medium={5} className="">
            <CustomCheckBox
              isChecked={isDriverWallet}
              onChecked={() => setIsDriverWallet((prev) => !prev)}
              label={t("pay_driver_money_to_company")}
            />
          </CustomView.Column>
          <CustomView.Column medium={7} className="">
            <CustomCheckBox
              isChecked={isAdminPozMachine}
              onChecked={() => setIsAdminPozMachine((prev) => !prev)}
              label={t("use_senjed_pos")}
            />
          </CustomView.Column>
        </CustomView.Row>

        <TitleLine title={t("companyDocs")} />
        <CustomView.Row className="form-row dropdown">
          {dropDownErrors.images !== "" && (
            <div
              className="dropdown"
              style={{ minHeight: "0.7rem", color: "red", fontSize: "0.7rem" }}
            >
              <div className="">{dropDownErrors.images}</div>
            </div>
          )}
          <CustomView.Column medium={4} className="">
            <ImageUploader
              defaultImage={selectedNationalCardImage}
              setImage={setSelectedNationalCardImage}
              uploadImage={(event: any) =>
                onUploadImage(event, setSelectedNationalCardImage)
              }
              label={t("nationalCardFrontImage")}
            />
          </CustomView.Column>{" "}
          <CustomView.Column medium={4} className="">
            <ImageUploader
              defaultImage={selectedNationalCardBackImage}
              setImage={setSelectedNationalCardBackImage}
              uploadImage={(event: any) =>
                onUploadImage(event, setSelectedNationalCardBackImage)
              }
              label={t("nationalCardBackImage")}
            />
          </CustomView.Column>{" "}
          <CustomView.Column medium={4} className="">
            <ImageUploader
              defaultImage={selectedCompanyLicenseImage}
              setImage={setSelectedCompanyLicenseImage}
              uploadImage={(event: any) =>
                onUploadImage(event, setSelectedCompanyLicenseImage)
              }
              label={t("companyLicence")}
            />
          </CustomView.Column>{" "}
        </CustomView.Row>

        <CustomView.Row className="form-row buttons-group">
          <CustomView.Column
            large={3}
            medium={4}
            small={6}
            className="form-colum"
          >
            <CustomButton2
              isLoading={isLoading}
              onClick={() => setShowModal("NONE")}
              title={t("cancel")}
              type="danger"
            />
          </CustomView.Column>
          <CustomView.Column
            large={3}
            medium={4}
            small={6}
            className="form-colum"
          >
            <CustomButton2
              isLoading={isLoading}
              onClick={submitHandler}
              title={t("submit")}
            />
          </CustomView.Column>
        </CustomView.Row>
      </CustomView.Container>
    </>
  );
};

export default Administrator;
